import {
  SampleFormData,
  Skrottlapp,
  SkrottlappParsed,
  Activity,
  StorfeData,
  ExternalReference,
  TilsynsobjektV2
} from '../types/types'
import { QueryClient } from '@tanstack/react-query'
import { IAuthContext } from '../hooks/useAuth'
import { getStorfeByIndividmerkeOptions } from '../hooks/useHusdyrregisteret'
import { getTilsynsobjekterOptions } from '../hooks/useTilsynsobjekt'
import { getReinbeitedistrikterQueryOptions } from '../hooks/useReinbeitedistrikter'
import { UserMessageError } from './userErrorUtils'

type AnimalKind = 'storfe' | 'svin' | 'småfe' | 'rein' | 'hest'

const getAnimalKindFromVarenummerPrefix = (
  prefix: number
): AnimalKind | undefined => {
  if (prefix >= 160 && prefix <= 169) return 'storfe'
  if (prefix >= 170 && prefix <= 176) return 'svin'
  if (prefix >= 180 && prefix <= 187) return 'småfe'
  if (prefix >= 190 && prefix <= 191) return 'rein'
  if (prefix == 188) return 'hest'
  return undefined
}

const verifyThatAnimalKindMatchesActivity = (
  animalKind: AnimalKind,
  activity: Activity
): boolean => {
  return (
    (activity === 'AKTIVITET$STORFEHOLD' && animalKind === 'storfe') ||
    (activity === 'AKTIVITET$SVINEHOLD' && animalKind === 'svin') ||
    (activity === 'AKTIVITET$SAUEHOLD' && animalKind === 'småfe') ||
    (activity === 'AKTIVITET$TAMREINDRIFT' && animalKind === 'rein')
  )
}

const animalSpecificPart = (
  code: string,
  animal: AnimalKind
): Partial<SkrottlappParsed> => {
  const commonPart = {
    foedeland: code.substring(50, 53),
    individnummer: code.substring(53, 65)
  }
  if (animal === 'storfe') {
    return {
      ...commonPart,
      alder: code.substring(65, 69),
      rase: code.substring(69, 71),
      srm: code.substring(71, 72)
    }
  }
  if (animal === 'rein') {
    return {
      ...commonPart,
      reinbeitedistrikt: code.substring(65, 68),
      alder: code.substring(68, 69),
      kjoenn: code.substring(69, 70)
    }
  }
  return commonPart
}

export const isOldSkrottlappCode = (code: string): boolean =>
  code.length === 26 && code.startsWith('4')
export const isNewSkrottlappCode = (code: string): boolean =>
  code.length === 72 && code.startsWith('2')

export const parseSkrottlappCode = (
  code: string,
  activity: Activity
): SkrottlappParsed => {
  if (isOldSkrottlappCode(code)) {
    return parseOldSkrottlappCode(code, activity)
  }
  if (isNewSkrottlappCode(code)) {
    return parseNewSkrottlappCode(code, activity)
  }
  throw new Error(`Ugyldig skrottlapp: ${code}`)
}

const parseOldSkrottlappCode = (
  code: string,
  activity: Activity
): SkrottlappParsed => {
  if (activity === 'AKTIVITET$TAMREINDRIFT') {
    return {
      reinbeitedistrikt: code.substring(1, 4),
      individnummer: code.substring(4, 16),
      alder: code.substring(16, 17),
      kjoenn: code.substring(17, 18)
    }
  }
  if (activity === 'AKTIVITET$STORFEHOLD') {
    return {
      foedeland: code.substring(1, 4),
      individnummer: code.substring(4, 16),
      alder: code.substring(16, 20),
      rase: code.substring(20, 22),
      srm: code.substring(25, 26)
    }
  }
  if (activity === 'AKTIVITET$SVINEHOLD' || activity === 'AKTIVITET$SAUEHOLD') {
    return {
      foedeland: code.substring(1, 4),
      individnummer: code.substring(4, 16)
    }
  }
  throw new Error('Aktivitet ikke støttet')
}

const parseNewSkrottlappCode = (
  code: string,
  activity: Activity
): SkrottlappParsed => {
  const varenummerPrefix = Number.parseInt(code.substring(15, 18), 10)
  const animalKind = getAnimalKindFromVarenummerPrefix(varenummerPrefix)
  if (!animalKind) throw new Error('Dyreart ikke støttet')
  if (!verifyThatAnimalKindMatchesActivity(animalKind, activity)) {
    throw new Error('Dyreart matcher ikke aktivitet')
  }
  return {
    eftakode: code.substring(1, 5),
    slaktedato: code.substring(5, 9),
    skrottnummer: code.substring(9, 15),
    varenummer: `${code.substring(15, 18)}XX${code.substring(20, 21)}`,
    leieslakt: code.substring(46, 50),
    ...animalSpecificPart(code, animalKind)
  }
}

interface AugmentationContext {
  storfe?: StorfeData
  tilsynsobjekter?: TilsynsobjektV2[]
  activity: Activity
}

export const augmentSkrottlappData = ({
  raw,
  parsed,
  context
}: {
  raw: string
  parsed: SkrottlappParsed
  context: AugmentationContext
}): Skrottlapp => {
  const produsentnummer = context.storfe?.produsentnr

  const filteredTilsynsobjekter = context.tilsynsobjekter?.filter(
    (tilsynsobjekt) =>
      tilsynsobjekt.identiteter.some(
        (identitetstype) =>
          identitetstype.identitetstype.id ===
            'IDENTITETSTYPE$PRODUSENTNUMMER' &&
          identitetstype.verdi === produsentnummer
      )
  )

  const tilsynsobjekt = filteredTilsynsobjekter?.find(
    (tilsynsobjekt) => tilsynsobjekt.aktivitet?.id === context.activity
  )

  const externalReferenceData: ExternalReference | undefined = tilsynsobjekt
    ? {
        externalReferenceId: produsentnummer,
        tilsynsobjektId: tilsynsobjekt.idstring,
        virksomhetsnavn: tilsynsobjekt.virksomhetsnavn,
        aktivitet: tilsynsobjekt.aktivitet?.beskrivelse ?? '',
        organisasjonsnummer: tilsynsobjekt.organisasjonsnummer
      }
    : undefined

  let gender: string | undefined = 'Unknown'
  if (context.activity === 'AKTIVITET$TAMREINDRIFT') {
    gender =
      parsed.kjoenn === '0'
        ? 'Male'
        : parsed.kjoenn === '1'
          ? 'Female'
          : undefined
  } else if (context.storfe?.kjonn === 'Kvige/ku') {
    gender = 'Female'
  } else if (context.storfe?.kjonn === 'Okse') {
    gender = 'Male'
  }

  const age =
    context.activity === 'AKTIVITET$TAMREINDRIFT'
      ? undefined
      : parsed?.alder != null && parsed.alder !== ''
        ? Number.parseInt(parsed.alder, 10)
        : undefined

  return {
    raw,
    parsed,
    additionalData: {
      produsent: externalReferenceData,
      entityId: parsed?.individnummer,
      gender,
      age: Number.isNaN(age) ? undefined : age,
      conditionAtSampling: 'Dead'
    }
  }
}

interface MapSkrottlappToFormData {
  fieldId: string
  formData: SampleFormData
  skrottlapp: Skrottlapp
  reinbeitedistrikt?: { codevalue: string; label: string }
}

const mapSkrottlappToFormData = ({
  fieldId,
  formData,
  skrottlapp,
  reinbeitedistrikt
}: MapSkrottlappToFormData) => {
  // Split the id to get the path in the form data
  const path = fieldId.split('_').slice(1) // Remove the initial 'root'
  const isSubSampleScan = /_subSamples_\d+_payload_/.test(fieldId)

  const updatedFormData = structuredClone(formData)
  // Create a reference to the current level in the form data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentLevel: any = updatedFormData

  // Set "produsent" in the root of the form data
  if (skrottlapp.additionalData?.produsent) {
    currentLevel['produsent'] = skrottlapp.additionalData.produsent
  }
  // Ensure payload exists
  if (!currentLevel['payload']) currentLevel['payload'] = {}
  if (reinbeitedistrikt && !isSubSampleScan) {
    const current = currentLevel['payload'].reinbeitedistrikt
    if (
      current == null ||
      current.codevalue == null ||
      current.codevalue === ''
    ) {
      currentLevel['payload'].reinbeitedistrikt = reinbeitedistrikt
    }
  }
  // Set the eftakode in the payload if present
  if (skrottlapp.parsed?.eftakode) {
    currentLevel['payload']['eftakode'] = skrottlapp.parsed.eftakode
  }
  if (!isSubSampleScan) {
    // Set the age in the payload if present
    if (skrottlapp.additionalData?.age != null) {
      currentLevel['payload']['age'] = skrottlapp.additionalData.age
    }
    // Set the gender in the payload if present
    if (skrottlapp.additionalData.gender) {
      currentLevel['payload']['gender'] = skrottlapp.additionalData.gender
    }
    // Set the conditionAtSampling in the payload if present
    if (skrottlapp.additionalData?.conditionAtSampling) {
      currentLevel['payload']['conditionAtSampling'] =
        skrottlapp.additionalData.conditionAtSampling
    }
  }

  // Traverse the path and create nested objects as needed
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!currentLevel[key]) {
      currentLevel[key] = Number.isNaN(Number(path[i + 1])) ? {} : []
    }
    currentLevel = currentLevel[key]
  }
  // Set the data at the correct location
  const leafKey = path.at(-1)
  if (leafKey == null) return updatedFormData
  currentLevel[leafKey] = skrottlapp.parsed
  // Attach entityId alongside the scanned data context (on the same parent level)
  if (skrottlapp.additionalData?.entityId) {
    currentLevel['entityId'] = skrottlapp.additionalData.entityId
  }

  return updatedFormData
}

const verifySkrottlappTilsynsobjekt = (
  savedFormData: Partial<SampleFormData> | undefined,
  skrottlapp: Skrottlapp
): boolean => {
  const skrottlappTilsynsobjektId =
    skrottlapp.additionalData.produsent?.tilsynsobjektId
  if (skrottlappTilsynsobjektId == null) {
    return true
  } else {
    return !(
      savedFormData?.produsent?.tilsynsobjektId != null &&
      skrottlappTilsynsobjektId !== savedFormData.produsent?.tilsynsobjektId
    )
  }
}

const fetchAdditionalStorfeData = async (
  queryClient: QueryClient,
  auth: IAuthContext,
  individnummer: string
): Promise<{ storfe: StorfeData; tilsynsobjekter: TilsynsobjektV2[] }> => {
  // 1. Fetch storfeindivid based on individmerke from skrottlapp
  const storfe = await queryClient.fetchQuery(
    getStorfeByIndividmerkeOptions(auth, individnummer)
  )

  // 2. Fetch tilsynsobjekter based on produsentnummer from storfeindivid
  const tilsynsobjekter = await queryClient.fetchQuery(
    getTilsynsobjekterOptions(auth, storfe.produsentnr)
  )
  return {
    storfe,
    tilsynsobjekter
  }
}

export const prepareSkrottlappAugmentation = async ({
  activity,
  code,
  auth,
  queryClient
}: {
  activity: Activity
  code: string
  auth: IAuthContext
  queryClient: QueryClient
}) => {
  // 1. Parse the skrottlapp barcode
  const skrottlappParsed = parseSkrottlappCode(code, activity)

  // 2. Fetch additional data (if activity is STORFEHOLD)
  const individnummer = skrottlappParsed.individnummer
  let additionalDataError: Error | undefined
  let reinbeitedistrikt: { codevalue: string; label: string } | undefined
  if (activity === 'AKTIVITET$TAMREINDRIFT') {
    try {
      const rawNumber = skrottlappParsed.reinbeitedistrikt ?? ''
      if (!/^\d+$/.test(rawNumber)) {
        throw new Error('Skannet distriktsnummer er ikke et gyldig tall')
      }
      const normalizedNumber = rawNumber.replace(/^0+/, '') || '0'
      const districts = await queryClient.fetchQuery(
        getReinbeitedistrikterQueryOptions(auth.authFetch, normalizedNumber)
      )
      const exactMatches = districts.filter(
        (district) => String(district.nr) === normalizedNumber
      )
      if (exactMatches.length !== 1) {
        throw new Error(
          exactMatches.length === 0
            ? 'Fant ikke et reinbeitedistrikt med dette nummeret'
            : 'Flere reinbeitedistrikter har dette nummeret'
        )
      }
      reinbeitedistrikt = {
        codevalue: exactMatches[0].codevalue,
        label: exactMatches[0].label
      }
    } catch {
      additionalDataError = new UserMessageError(
        'Kunne ikke slå opp reinbeitedistriktet. Velg reinbeitedistrikt manuelt.'
      )
    }
  }

  const additionalData =
    individnummer != null && activity === 'AKTIVITET$STORFEHOLD'
      ? await fetchAdditionalStorfeData(queryClient, auth, individnummer).catch(
          (err: unknown) => {
            additionalDataError =
              err instanceof Error
                ? err
                : new Error(
                    'Det har oppstått en feil ved henting av skrottlappdata'
                  )
            return undefined
          }
        )
      : undefined
  const skrottlapp = augmentSkrottlappData({
    raw: code,
    parsed: skrottlappParsed,
    context: {
      storfe: additionalData?.storfe,
      tilsynsobjekter: additionalData?.tilsynsobjekter,
      activity
    }
  })
  return {
    skrottlapp,
    additionalDataError,
    applyWithWarnings: (
      fieldId: string,
      latestFormData: SampleFormData
    ): { formData: SampleFormData; warning?: string } => {
      // 4. Verify that the skrottlapp has the same "tilsynsobjektId" as the one that is selected in the form (if any)
      if (!verifySkrottlappTilsynsobjekt(latestFormData, skrottlapp)) {
        throw new Error(
          'Skrottlappen har ikke samme produsent som valgt i skjemaet'
        )
      }
      const selectedDistrict = latestFormData.payload?.reinbeitedistrikt
      const hasDifferentDistrict =
        activity === 'AKTIVITET$TAMREINDRIFT' &&
        selectedDistrict != null &&
        selectedDistrict.codevalue != null &&
        selectedDistrict.codevalue !== '' &&
        reinbeitedistrikt != null &&
        selectedDistrict.codevalue !== reinbeitedistrikt.codevalue
      return {
        formData: mapSkrottlappToFormData({
          fieldId,
          formData: latestFormData,
          skrottlapp,
          reinbeitedistrikt
        }),
        warning: hasDifferentDistrict
          ? 'Skannet reinbeitedistrikt er forskjellig fra valgt distrikt. Kontroller distriktet manuelt.'
          : undefined
      }
    },
    apply: (
      fieldId: string,
      latestFormData: SampleFormData
    ): SampleFormData => {
      if (!verifySkrottlappTilsynsobjekt(latestFormData, skrottlapp)) {
        throw new Error(
          'Skrottlappen har ikke samme produsent som valgt i skjemaet'
        )
      }
      return mapSkrottlappToFormData({
        fieldId,
        formData: latestFormData,
        skrottlapp,
        reinbeitedistrikt
      })
    }
  }
}
